import type {Command, EditorView, KeyBinding} from '@codemirror/view'
import {ActionBar, type IconButtonProps} from '@primer/react'
import type {Nodes} from 'mdast'
import {use} from 'react'

import type {SelectionContextValue} from '../codemirror/extensions/selection-context'
import {useHybridEditorTelemetry} from '../telemetry'
import {ToolbarContext} from './ToolbarContext'
import {formatKeybindingHint} from './util'

interface ToolbarButtonProps extends Omit<
  IconButtonProps,
  'onClick' | 'onMouseDown' | 'variant' | 'aria-labelledby' | 'aria-pressed'
> {
  command: Command | KeyBinding
  matchingNodeFilter: (node: Nodes) => boolean
  ['aria-label']: string
  telemetryId?: string
  resolveIsPressed?: (selectionContext: SelectionContextValue, defaultPressed: boolean) => boolean
}

export const ToolbarButton = ({
  command,
  matchingNodeFilter,
  resolveIsPressed,
  telemetryId,
  ...props
}: ToolbarButtonProps) => {
  const {view, selectedNodes} = use(ToolbarContext)
  const defaultIsPressed = selectedNodes.some(matchingNodeFilter)
  const isPressed = resolveIsPressed?.(selectedNodes, defaultIsPressed) ?? defaultIsPressed
  const telemetry = useHybridEditorTelemetry()

  const ariaLabel = props['aria-label']

  const commandIsKeybinding = typeof command !== 'function'
  const fallbackCommand: Command = () => false
  const commandFn = (commandIsKeybinding ? command.run : command) ?? fallbackCommand
  const commandHint = commandIsKeybinding ? command.key : undefined

  const runCommand = () => {
    if (!view) return

    // Toolbar clicks can steal focus, so ensure the editor is focused before invoking CodeMirror commands.
    if (!view.hasFocus) view.focus()

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- Some commands accept optional event source metadata.
    const runWithEventSource = commandFn as (editorView: EditorView, eventSource?: 'keyboard' | 'toolbar') => boolean
    const handled = runWithEventSource(view, 'toolbar')

    telemetry.trackToolbarInteraction({
      control: telemetryId ?? ariaLabel ?? 'unknown',
      action: telemetryId ?? ariaLabel ?? 'unknown',
      trigger: 'toolbar',
      isActive: isPressed,
      keybinding: commandHint,
      handled,
    })
  }

  return (
    <ActionBar.IconButton
      variant="invisible"
      // Prevent focus leaving input:
      onMouseDown={e => e.preventDefault()}
      keybindingHint={formatKeybindingHint(commandHint)}
      disabled={!view}
      onClick={runCommand}
      aria-pressed={isPressed}
      {...props}
    />
  )
}

ToolbarButton.displayName = 'MarkdownEditor.ToolbarButton'
